import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { overtimeSatisticApi } from '../../../apis';
import {
  EmployeeRelatedInformationDtoRolesEnum,
  StatisticsOvertimeOverviewJudgmentEnum,
} from '../../../apis/client-axios';
import TableWrap from '../../../components/TableWrap';
import Widget from '../../../components/Widget';
import { OVERTIME_SATISTIC } from '../../../constants/QueryKey';
import { RootState } from '../../../store';
import './style.scss';

const EmployeeConfirm = () => {
  const intl = useIntl();
  const employeeId = useSelector((state: RootState) => state.auth.authUser?.id);
  const year = dayjs().year();
  const employeeRole = useSelector((state: RootState) => state.auth.authUser?.role);
  const navigate = useNavigate();
  const { data: overTimeData, isLoading } = useQuery({
    queryKey: [OVERTIME_SATISTIC],
    queryFn: () => overtimeSatisticApi.overtimeControllerStatisticsOvertime(year as number, employeeId as string),
  });

  useEffect(() => {
    if (employeeRole && employeeRole !== EmployeeRelatedInformationDtoRolesEnum.Employee) {
      navigate(-1);
    }
  }, [navigate, employeeRole]);

  const externalData = [
    {
      regulation: intl.formatMessage(
        { id: 'annual.overtime.detail.column.yearlyOvertimeLimit' },
        { yearlyOvertimeLimit: overTimeData?.data.data?.overtimeSettings.yearlyOvertimeLimit },
      ),
    },
    {
      regulation: intl.formatMessage(
        { id: 'annual.overtime.detail.column.avgMonthlyOvertime' },
        { avgMonthlyOvertime: overTimeData?.data.data?.overtimeSettings.avgMonthlyOvertime },
      ),
    },
    {
      regulation: intl.formatMessage(
        { id: 'annual.overtime.detail.column.singleMonthLimit' },
        { singleMonthLimit: overTimeData?.data.data?.overtimeSettings.singleMonthLimit },
      ),
    },
    {
      regulation: intl.formatMessage(
        { id: 'annual.overtime.detail.column.overtimeExceed' },
        { overtimeExceed: overTimeData?.data.data?.overtimeSettings.overtimeExceed },
      ),
    },
  ];

  const dataSource = overTimeData?.data.data?.overview?.map((item, index) => ({
    ...item,
    regulation: externalData[index]?.regulation,
  }));

  const regulation = [
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.regulation' }),
      dataIndex: 'regulation',
      key: 'regulation',
      render: (value, record) => {
        return <p className="employee-confirm_detail-table-column-data">{record?.regulation}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.judgement' }),
      dataIndex: 'judgment',
      key: 'judgment',
      render: (value, record) => {
        return (
          <p className={`employee-confirm_detail-table-column-data`}>
            {intl.formatMessage({ id: `annual.overtime.detail.column.judgment.${record?.judgment}` })}
          </p>
        );
      },
    },

    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.asToday' }),
      dataIndex: 'asOfToday',
      key: 'asOfToday',
      render: (value, record) => {
        return <p className="employee-confirm_detail-table-column-data">{record?.asOfToday}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.upper' }),
      dataIndex: 'upperLimit',
      key: 'upperLimit',
      render: (value, record) => {
        return <p className="employee-confirm_detail-table-column-data">{record?.upperLimit}</p>;
      },
    },
  ];
  const columns = [
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.year' }, { year: year }),
      dataIndex: 'month',
      key: 'month',
      render: (value, record, index) => {
        const columnMonth = record?.month;
        return (
          <p className="employee-confirm_detail-table-column-data">
            {columnMonth === 'all'
              ? intl.formatMessage({ id: 'annual.overtime.detail.column.all' })
              : intl.formatMessage({ id: 'annual.overtime.detail.column.month' }, { month: columnMonth })}
          </p>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.weekdays' }),
      dataIndex: 'weekday',
      key: 'weekday',
      render: (record) => {
        return <p className="employee-confirm_detail-table-column-data">{record}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.scheduled' }),
      dataIndex: 'designatedHoliday',
      key: 'designatedHoliday',
      render: (record) => {
        return <p className="employee-confirm_detail-table-column-data">{record}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.both' }),
      dataIndex: 'weekdayPlusDesignated',
      key: 'weekdayPlusDesignated',
      render: (record) => {
        return <p className="employee-confirm_detail-table-column-data">{record}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.statutory' }),
      dataIndex: 'legalHoliday',
      key: 'legalHoliday',
      render: (record) => {
        return <p className="employee-confirm_detail-table-column-data">{record}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.total' }),
      dataIndex: 'total',
      key: 'total',
      render: (record) => {
        return <p className="employee-confirm_detail-table-column-data">{record}</p>;
      },
    },
  ];
  return (
    <div className="employee-confirm_detail">
      <Widget
        breadcrumbs={[intl.formatMessage({ id: 'employee.confirm.breadcumb' })]}
        content={
          <div className="employee-confirm_detail-content">
            <TableWrap
              isPagination={false}
              loading={isLoading}
              tableProps={{
                dataSource: dataSource,
                columns: regulation,
                className: 'employee-confirm_detail-content-regulation',
                rowClassName: (record) => {
                  return record.judgment === StatisticsOvertimeOverviewJudgmentEnum.Ok
                    ? 'judgement-ok'
                    : record.judgment === StatisticsOvertimeOverviewJudgmentEnum.Ng
                      ? 'judgement-ng'
                      : record.judgment === StatisticsOvertimeOverviewJudgmentEnum.Notice
                        ? 'judgement-notice'
                        : '';
                },
              }}
            />
            <TableWrap
              isPagination={false}
              loading={isLoading}
              tableProps={{
                dataSource: overTimeData?.data.data?.detail || [],
                columns,
                className: 'employee-confirm_detail-content-table',
              }}
            />
          </div>
        }
        title={intl.formatMessage({ id: 'employee.confirm.breadcumb' })}
      />
    </div>
  );
};
export default EmployeeConfirm;
